import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Calendar, Users, FileText, Clock, PlusCircle, Settings, LogOut, DollarSign, Send, Play, Square, Receipt } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const [loggedIn, setLoggedIn] = useState(false);
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [revenue, setRevenue] = useState({});
  const [bookings, setBookings] = useState({ astrology: [], vastu: [], enquiries: [] });
  const [blogForm, setBlogForm] = useState({ title_en: '', title_hi: '', content_en: '', content_hi: '', category: 'astrology', image_url: '' });
  const [timerActive, setTimerActive] = useState(false);
  const [timerBookingId, setTimerBookingId] = useState('');
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerClient, setTimerClient] = useState(null); // { client_name, client_phone, package_name, first_time_free_used, rate_per_minute, free_minutes }
  const [sessionNotes, setSessionNotes] = useState('');
  const [lastInvoice, setLastInvoice] = useState(null); // last completed session summary for display
  const timerRef = useRef(null);
  const [invoiceForm, setInvoiceForm] = useState({ enquiry_id: '', travel_cost: 0, stay_cost: 0, consultation_fee: 0 });
  const [availForm, setAvailForm] = useState({ date: '', available: true, custom_slots: '' });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      setToken(res.data.token);
      setLoggedIn(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    }
  };

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const [revRes, bookRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/revenue`, { headers: headers() }),
        axios.get(`${API}/admin/bookings`, { headers: headers() }),
        axios.get(`${API}/admin/users`, { headers: headers() })
      ]);
      setRevenue(revRes.data);
      setBookings(bookRes.data);
      setUsers(usersRes.data);
    } catch (err) { console.error(err); }
  }, [token, headers]);

  useEffect(() => { if (loggedIn) fetchData(); }, [loggedIn, fetchData]);

  useEffect(() => {
    if (timerActive) {
      timerRef.current = setInterval(() => setTimerSeconds(s => s + 1), 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [timerActive]);

  const handleBookingAction = async (bookingId, bookingType, action) => {
    try {
      await axios.post(`${API}/admin/booking/action`, { booking_id: bookingId, booking_type: bookingType, action }, { headers: headers() });
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleStartTimer = async (bookingId) => {
    try {
      const res = await axios.post(`${API}/admin/session/timer`, { booking_id: bookingId, action: 'start' }, { headers: headers() });
      setTimerBookingId(bookingId);
      setTimerSeconds(0);
      setTimerClient(res.data);
      setSessionNotes('');
      setLastInvoice(null);
      setTimerActive(true);
      setActiveTab('timer');
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleStopTimer = async () => {
    if (!window.confirm('End session and generate invoice?')) return;
    try {
      const res = await axios.post(`${API}/admin/session/timer`, { booking_id: timerBookingId, action: 'stop', notes: sessionNotes }, { headers: headers() });
      setTimerActive(false);
      setLastInvoice(res.data);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleCreateBlog = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/blog/create`, blogForm, { headers: headers() });
      setBlogForm({ title_en: '', title_hi: '', content_en: '', content_hi: '', category: 'astrology', image_url: '' });
      alert('Blog post created!');
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/custom-invoice`, invoiceForm, { headers: headers() });
      alert(`Invoice created! Total: ₹${res.data.total_amount}\nPayment Link: ${res.data.payment_link}`);
      setInvoiceForm({ enquiry_id: '', travel_cost: 0, stay_cost: 0, consultation_fee: 0 });
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleSetAvailability = async (e) => {
    e.preventDefault();
    try {
      const slotsArr = availForm.custom_slots ? availForm.custom_slots.split(',').map(s => s.trim()) : null;
      await axios.post(`${API}/admin/availability`, { date: availForm.date, available: availForm.available, custom_slots: slotsArr }, { headers: headers() });
      alert('Availability updated!');
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  const handleGenerateReport = async (bookingId, bookingType) => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/generate-report?booking_id=${bookingId}&booking_type=${bookingType}`, {}, { headers: headers() });
      alert('AI Session Report generated!');
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    setLoading(false);
  };

  const formatTimer = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  const getWhatsAppLink = (booking) => {
    const msg = encodeURIComponent(`Message from Astrology Times App\nNew Booking!\nClient: ${booking.client_name}\nService: ${booking.package_type || booking.service_type}\nDate: ${booking.booking_date}\nTime: ${booking.booking_time}\nPhone: ${booking.client_phone || 'N/A'}\nAmount: ₹${booking.amount}\nBooking ID: ${booking.booking_id}`);
    return `https://wa.me/918099975662?text=${msg}`;
  };

  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
        <div className="w-full max-w-md bg-[#1A1A1A] border border-[#C9A84C]/15 p-8">
          <h2 className="text-3xl font-serif text-[#F5F2EB] text-center mb-6">Admin Login</h2>
          {error && <div className="bg-red-500/10 border border-red-500/50 text-red-500 px-4 py-3 mb-4 text-sm">{error}</div>}
          <form onSubmit={handleLogin} className="space-y-4">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Admin Email"
              className="w-full bg-transparent border-b border-[#C9A84C]/30 py-3 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" required data-testid="admin-email-input" />
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
              className="w-full bg-transparent border-b border-[#C9A84C]/30 py-3 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" required data-testid="admin-password-input" />
            <button type="submit" className="w-full bg-[#C9A84C] text-[#0A0A0A] py-3 font-medium hover:bg-[#E5C870] uppercase tracking-wider text-sm" data-testid="admin-login-btn">
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'dashboard', icon: BarChart3, label: 'Dashboard' },
    { key: 'bookings', icon: Calendar, label: 'Bookings' },
    { key: 'enquiries', icon: FileText, label: 'Enquiries' },
    { key: 'timer', icon: Clock, label: 'Timer' },
    { key: 'blog', icon: PlusCircle, label: 'Blog' },
    { key: 'invoices', icon: Receipt, label: 'Invoices' },
    { key: 'availability', icon: Settings, label: 'Availability' },
    { key: 'users', icon: Users, label: 'Users' }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-24 px-6 md:px-12 pb-16" data-testid="admin-panel">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-serif text-[#F5F2EB]">Admin Panel</h1>
        <button onClick={() => { setLoggedIn(false); setToken(''); }} className="flex items-center gap-2 text-[#A3A3A3] hover:text-[#C9A84C]">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>

      {timerActive && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 bg-[#C9A84C] text-[#0A0A0A] px-8 py-4 flex items-center gap-6 shadow-lg" data-testid="session-timer-bar">
          <Clock className="w-6 h-6" />
          <span className="text-3xl font-bold font-mono">{formatTimer(timerSeconds)}</span>
          <span className="text-sm">Booking: {timerBookingId}</span>
          <button onClick={handleStopTimer} className="bg-[#0A0A0A] text-[#C9A84C] px-4 py-2 flex items-center gap-2 hover:bg-[#1A1A1A]">
            <Square className="w-4 h-4" /> Stop
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-8">
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm ${activeTab === tab.key ? 'bg-[#C9A84C] text-[#0A0A0A]' : 'bg-[#1A1A1A] border border-[#C9A84C]/15 text-[#A3A3A3] hover:border-[#C9A84C]/30'}`}>
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'dashboard' && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-6">
              <DollarSign className="w-8 h-8 text-[#C9A84C] mb-2" />
              <p className="text-[#A3A3A3] text-xs">Today</p>
              <p className="text-2xl font-bold text-[#F5F2EB]">₹{revenue.daily_revenue || 0}</p>
            </div>
            <div className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-6">
              <DollarSign className="w-8 h-8 text-[#C9A84C] mb-2" />
              <p className="text-[#A3A3A3] text-xs">This Week</p>
              <p className="text-2xl font-bold text-[#F5F2EB]">₹{revenue.weekly_revenue || 0}</p>
            </div>
            <div className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-6">
              <DollarSign className="w-8 h-8 text-[#C9A84C] mb-2" />
              <p className="text-[#A3A3A3] text-xs">This Month</p>
              <p className="text-2xl font-bold text-[#F5F2EB]">₹{revenue.monthly_revenue || 0}</p>
            </div>
            <div className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-6">
              <DollarSign className="w-8 h-8 text-[#C9A84C] mb-2" />
              <p className="text-[#A3A3A3] text-xs">Total Revenue</p>
              <p className="text-2xl font-bold text-[#C9A84C]">₹{revenue.total_revenue || 0}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-6">
              <Users className="w-6 h-6 text-[#C9A84C] mb-2" />
              <p className="text-[#A3A3A3] text-xs">Total Users</p>
              <p className="text-xl font-bold text-[#F5F2EB]">{revenue.total_users || 0}</p>
            </div>
            <div className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-6">
              <Calendar className="w-6 h-6 text-[#C9A84C] mb-2" />
              <p className="text-[#A3A3A3] text-xs">Total Bookings</p>
              <p className="text-xl font-bold text-[#F5F2EB]">{revenue.total_bookings || 0}</p>
            </div>
            <div className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-6">
              <Clock className="w-6 h-6 text-yellow-400 mb-2" />
              <p className="text-[#A3A3A3] text-xs">Pending Bookings</p>
              <p className="text-xl font-bold text-yellow-400">{revenue.pending_bookings || 0}</p>
            </div>
            <div className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-6">
              <FileText className="w-6 h-6 text-yellow-400 mb-2" />
              <p className="text-[#A3A3A3] text-xs">Pending Enquiries</p>
              <p className="text-xl font-bold text-yellow-400">{revenue.pending_enquiries || 0}</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'bookings' && (
        <div>
          <h2 className="text-xl font-serif text-[#F5F2EB] mb-4">Astrology Bookings ({bookings.astrology.length})</h2>
          <div className="overflow-x-auto mb-8">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#C9A84C]/20">
                <th className="text-left py-3 text-[#A3A3A3]">ID</th><th className="text-left py-3 text-[#A3A3A3]">Client</th><th className="text-left py-3 text-[#A3A3A3]">Package</th><th className="text-left py-3 text-[#A3A3A3]">Date</th><th className="text-left py-3 text-[#A3A3A3]">Amount</th><th className="text-left py-3 text-[#A3A3A3]">Status</th><th className="text-left py-3 text-[#A3A3A3]">Actions</th>
              </tr></thead>
              <tbody>
                {bookings.astrology.map(b => (
                  <tr key={b.booking_id} className="border-b border-[#C9A84C]/10">
                    <td className="py-3 text-[#F5F2EB]">{b.booking_id}</td>
                    <td className="py-3 text-[#F5F2EB]">{b.client_name}</td>
                    <td className="py-3 text-[#A3A3A3]">{b.package_type}</td>
                    <td className="py-3 text-[#A3A3A3]">{b.booking_date} {b.booking_time}</td>
                    <td className="py-3 text-[#C9A84C]">₹{b.amount}</td>
                    <td className="py-3"><span className={`${b.status === 'confirmed' ? 'text-green-400' : b.status === 'pending' ? 'text-yellow-400' : 'text-red-400'}`}>{b.status}</span></td>
                    <td className="py-3 flex gap-2 flex-wrap">
                      {b.status === 'pending' && <button onClick={() => handleBookingAction(b.booking_id, 'astrology', 'confirmed')} className="text-green-400 hover:underline text-xs">Confirm</button>}
                      {b.status !== 'cancelled' && <button onClick={() => handleBookingAction(b.booking_id, 'astrology', 'cancelled')} className="text-red-400 hover:underline text-xs">Cancel</button>}
                      <button onClick={() => handleStartTimer(b.booking_id)} className="text-[#C9A84C] hover:underline text-xs flex items-center gap-1"><Play className="w-3 h-3" />Timer</button>
                      <button onClick={() => handleGenerateReport(b.booking_id, 'astrology')} disabled={loading} className="text-blue-400 hover:underline text-xs">AI Report</button>
                      <a href={getWhatsAppLink(b)} target="_blank" rel="noopener noreferrer" className="text-green-400 hover:underline text-xs flex items-center gap-1"><Send className="w-3 h-3" />WA</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="text-xl font-serif text-[#F5F2EB] mb-4">Vastu Bookings ({bookings.vastu.length})</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#C9A84C]/20">
                <th className="text-left py-3 text-[#A3A3A3]">ID</th><th className="text-left py-3 text-[#A3A3A3]">Client</th><th className="text-left py-3 text-[#A3A3A3]">Service</th><th className="text-left py-3 text-[#A3A3A3]">Date</th><th className="text-left py-3 text-[#A3A3A3]">Amount</th><th className="text-left py-3 text-[#A3A3A3]">Status</th><th className="text-left py-3 text-[#A3A3A3]">Actions</th>
              </tr></thead>
              <tbody>
                {bookings.vastu.map(b => (
                  <tr key={b.booking_id} className="border-b border-[#C9A84C]/10">
                    <td className="py-3 text-[#F5F2EB]">{b.booking_id}</td>
                    <td className="py-3 text-[#F5F2EB]">{b.client_name}</td>
                    <td className="py-3 text-[#A3A3A3]">{b.service_type}</td>
                    <td className="py-3 text-[#A3A3A3]">{b.booking_date} {b.booking_time}</td>
                    <td className="py-3 text-[#C9A84C]">₹{b.amount}</td>
                    <td className="py-3"><span className={`${b.status === 'confirmed' ? 'text-green-400' : b.status === 'pending' ? 'text-yellow-400' : 'text-red-400'}`}>{b.status}</span></td>
                    <td className="py-3 flex gap-2 flex-wrap">
                      {b.status === 'pending' && <button onClick={() => handleBookingAction(b.booking_id, 'vastu', 'confirmed')} className="text-green-400 hover:underline text-xs">Confirm</button>}
                      {b.status !== 'cancelled' && <button onClick={() => handleBookingAction(b.booking_id, 'vastu', 'cancelled')} className="text-red-400 hover:underline text-xs">Cancel</button>}
                      <button onClick={() => handleGenerateReport(b.booking_id, 'vastu')} disabled={loading} className="text-blue-400 hover:underline text-xs">AI Report</button>
                      <a href={getWhatsAppLink(b)} target="_blank" rel="noopener noreferrer" className="text-green-400 hover:underline text-xs flex items-center gap-1"><Send className="w-3 h-3" />WA</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'enquiries' && (
        <div>
          <h2 className="text-xl font-serif text-[#F5F2EB] mb-4">Site Visit Enquiries ({bookings.enquiries.length})</h2>
          {bookings.enquiries.map(e => (
            <div key={e.enquiry_id} className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-4 mb-3">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-[#F5F2EB] font-medium">{e.name} - {e.city}, {e.state}</p>
                  <p className="text-[#A3A3A3] text-sm">{e.property_type} | {e.property_size} sq ft | {e.preferred_visit_date}</p>
                  <p className="text-[#A3A3A3] text-sm">Phone: {e.phone} | Email: {e.client_email}</p>
                  {e.issues && <p className="text-[#A3A3A3] text-sm mt-1">Issues: {e.issues}</p>}
                  {e.payment_link && <p className="text-[#C9A84C] text-sm mt-1">Payment Link: <a href={e.payment_link} target="_blank" rel="noopener noreferrer" className="underline">{e.payment_link}</a></p>}
                </div>
                <span className={`text-xs uppercase px-2 py-1 ${e.status === 'pending' ? 'bg-yellow-400/10 text-yellow-400' : 'bg-green-400/10 text-green-400'}`}>{e.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'timer' && (
        <div className="max-w-3xl">
          <h2 className="text-xl font-serif text-[#F5F2EB] mb-4">Per-Minute Session</h2>

          {!timerActive && !lastInvoice && (
            <div className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-8 text-center">
              <Clock className="w-12 h-12 text-[#C9A84C] mx-auto mb-4" />
              <p className="text-[#F5F2EB] text-lg mb-2">No active session</p>
              <p className="text-[#A3A3A3] text-sm mb-6">Go to <span className="text-[#C9A84C]">Bookings</span> tab and click <span className="text-[#C9A84C]">Start Timer</span> on a confirmed booking.</p>
              <div className="inline-block bg-[#0A0A0A] border border-[#C9A84C]/20 p-4 text-left text-sm text-[#A3A3A3]">
                <div><span className="text-[#C9A84C]">Rate:</span> ₹60 per minute</div>
                <div><span className="text-[#C9A84C]">New Client Bonus:</span> First 10 minutes free (one-time)</div>
                <div><span className="text-[#C9A84C]">Rounding:</span> Partial minutes rounded up</div>
              </div>
            </div>
          )}

          {timerActive && (() => {
            const minutesElapsed = Math.floor(timerSeconds / 60);
            const billedMinutes = Math.max(1, Math.ceil(timerSeconds / 60));
            const rate = timerClient?.rate_per_minute || 60;
            const freeMins = Math.min(timerClient?.free_minutes || 0, billedMinutes);
            const chargeable = Math.max(0, billedMinutes - freeMins);
            const runningTotal = chargeable * rate;
            return (
              <div className="space-y-6">
                {/* Client info */}
                <div className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-6" data-testid="timer-client-info">
                  <h3 className="text-sm text-[#A3A3A3] uppercase tracking-wider mb-3">Client</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[#F5F2EB]">
                    <div><span className="text-[#A3A3A3] text-xs">Name:</span> <span className="ml-2">{timerClient?.client_name || '—'}</span></div>
                    <div><span className="text-[#A3A3A3] text-xs">Phone:</span> <span className="ml-2">{timerClient?.client_phone || '—'}</span></div>
                    <div><span className="text-[#A3A3A3] text-xs">Email:</span> <span className="ml-2">{timerClient?.client_email || '—'}</span></div>
                    <div><span className="text-[#A3A3A3] text-xs">Package:</span> <span className="ml-2">{timerClient?.package_name || 'Per-minute'}</span></div>
                    <div className="md:col-span-2"><span className="text-[#A3A3A3] text-xs">Booking ID:</span> <span className="ml-2 font-mono text-[#C9A84C]">{timerBookingId}</span></div>
                  </div>
                </div>

                {/* Big timer + running total */}
                <div className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-8 text-center">
                  <div className="text-7xl font-mono text-[#C9A84C] mb-2" data-testid="timer-display">{formatTimer(timerSeconds)}</div>
                  <p className="text-[#A3A3A3] text-xs uppercase tracking-widest mb-6">Elapsed</p>

                  {/* Calculation breakdown */}
                  <div className="bg-[#0A0A0A] border border-[#C9A84C]/20 p-4 text-sm text-left max-w-md mx-auto mb-6" data-testid="timer-breakdown">
                    <div className="flex justify-between py-1"><span className="text-[#A3A3A3]">Minutes elapsed</span><span className="text-[#F5F2EB] font-mono">{minutesElapsed}</span></div>
                    <div className="flex justify-between py-1"><span className="text-[#A3A3A3]">Billed minutes (round up)</span><span className="text-[#F5F2EB] font-mono">{billedMinutes}</span></div>
                    <div className="flex justify-between py-1"><span className="text-[#A3A3A3]">Rate per minute</span><span className="text-[#F5F2EB] font-mono">₹{rate}</span></div>
                    {freeMins > 0 && (
                      <div className="flex justify-between py-1 text-green-400"><span>First-time free</span><span className="font-mono">− {freeMins} min (−₹{freeMins * rate})</span></div>
                    )}
                    <div className="flex justify-between py-1 border-t border-[#C9A84C]/20 mt-2 pt-2"><span className="text-[#A3A3A3]">Chargeable minutes</span><span className="text-[#F5F2EB] font-mono">{chargeable}</span></div>
                    <div className="flex justify-between py-2 border-t border-[#C9A84C]/40 mt-2"><span className="text-[#C9A84C] uppercase tracking-wider text-xs">Running Total</span><span className="text-[#C9A84C] font-mono text-2xl" data-testid="timer-running-total">₹{runningTotal}</span></div>
                  </div>

                  <button onClick={handleStopTimer} className="bg-red-500 text-white px-10 py-3 flex items-center gap-2 mx-auto hover:bg-red-600" data-testid="timer-stop-btn">
                    <Square className="w-5 h-5" /> End Session & Generate Invoice
                  </button>
                </div>

                {/* Notes */}
                <div className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-6">
                  <label className="text-sm text-[#A3A3A3] uppercase tracking-wider block mb-2">Session Notes <span className="text-xs normal-case text-[#A3A3A3]/70">(saved with invoice)</span></label>
                  <textarea
                    value={sessionNotes}
                    onChange={(e) => setSessionNotes(e.target.value)}
                    rows={6}
                    placeholder="Key points from the reading, remedies suggested, follow-up recommendations..."
                    className="w-full bg-[#0A0A0A] border border-[#C9A84C]/30 p-3 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none text-sm"
                    data-testid="timer-notes-textarea"
                  />
                  <p className="text-xs text-[#A3A3A3] mt-2">{sessionNotes.length} characters</p>
                </div>
              </div>
            );
          })()}

          {!timerActive && lastInvoice && (
            <div className="bg-[#1A1A1A] border border-[#C9A84C]/30 p-8" data-testid="timer-invoice-summary">
              <div className="flex items-center gap-3 mb-6">
                <Receipt className="w-6 h-6 text-[#C9A84C]" />
                <h3 className="text-xl font-serif text-[#F5F2EB]">Session Ended — Invoice Generated</h3>
              </div>
              <div className="bg-[#0A0A0A] border border-[#C9A84C]/20 p-6 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[#A3A3A3]">Invoice ID</span><span className="font-mono text-[#C9A84C]">{lastInvoice.invoice_id}</span></div>
                <div className="flex justify-between"><span className="text-[#A3A3A3]">Total Duration</span><span className="text-[#F5F2EB] font-mono">{lastInvoice.duration_minutes} min</span></div>
                <div className="flex justify-between"><span className="text-[#A3A3A3]">Rate</span><span className="text-[#F5F2EB] font-mono">₹{lastInvoice.rate_per_minute}/min</span></div>
                <div className="flex justify-between"><span className="text-[#A3A3A3]">Gross Amount</span><span className="text-[#F5F2EB] font-mono">₹{lastInvoice.gross_amount}</span></div>
                {lastInvoice.free_minutes_applied > 0 && (
                  <div className="flex justify-between text-green-400"><span>First-time Free ({lastInvoice.free_minutes_applied} min)</span><span className="font-mono">− ₹{lastInvoice.discount_amount}</span></div>
                )}
                <div className="flex justify-between border-t border-[#C9A84C]/30 pt-3 mt-3"><span className="text-[#A3A3A3]">Billable Minutes</span><span className="text-[#F5F2EB] font-mono">{lastInvoice.billable_minutes}</span></div>
                <div className="flex justify-between border-t border-[#C9A84C]/40 pt-3 mt-2"><span className="text-[#C9A84C] uppercase tracking-wider text-xs">Total Payable</span><span className="text-[#C9A84C] font-mono text-3xl" data-testid="invoice-final-amount">₹{lastInvoice.final_amount}</span></div>
                {lastInvoice.notes_saved && <div className="text-xs text-green-400 pt-2">✓ Session notes saved to invoice PDF</div>}
              </div>
              <button onClick={() => setLastInvoice(null)} className="mt-6 bg-[#C9A84C] text-[#0A0A0A] px-6 py-2 hover:bg-[#E5C870] uppercase tracking-wider text-xs" data-testid="invoice-close-btn">Start New Session</button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'blog' && (
        <div className="max-w-2xl">
          <h2 className="text-xl font-serif text-[#F5F2EB] mb-4">Create Blog Post</h2>
          <form onSubmit={handleCreateBlog} className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-6 space-y-4">
            <div><label className="text-xs text-[#A3A3A3] uppercase">Title (English)</label>
              <input value={blogForm.title_en} onChange={(e) => setBlogForm({ ...blogForm, title_en: e.target.value })} className="w-full bg-transparent border-b border-[#C9A84C]/30 py-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" required data-testid="blog-title-en" /></div>
            <div><label className="text-xs text-[#A3A3A3] uppercase">Title (Hindi)</label>
              <input value={blogForm.title_hi} onChange={(e) => setBlogForm({ ...blogForm, title_hi: e.target.value })} className="w-full bg-transparent border-b border-[#C9A84C]/30 py-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" required data-testid="blog-title-hi" /></div>
            <div><label className="text-xs text-[#A3A3A3] uppercase">Content (English)</label>
              <textarea value={blogForm.content_en} onChange={(e) => setBlogForm({ ...blogForm, content_en: e.target.value })} rows={4} className="w-full bg-transparent border border-[#C9A84C]/30 p-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" required /></div>
            <div><label className="text-xs text-[#A3A3A3] uppercase">Content (Hindi)</label>
              <textarea value={blogForm.content_hi} onChange={(e) => setBlogForm({ ...blogForm, content_hi: e.target.value })} rows={4} className="w-full bg-transparent border border-[#C9A84C]/30 p-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" required /></div>
            <div><label className="text-xs text-[#A3A3A3] uppercase">Category</label>
              <select value={blogForm.category} onChange={(e) => setBlogForm({ ...blogForm, category: e.target.value })} className="w-full bg-[#1A1A1A] border-b border-[#C9A84C]/30 py-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none">
                <option value="astrology">Astrology</option><option value="vastu">Vastu</option><option value="panchang">Panchang</option><option value="remedies">Remedies</option>
              </select></div>
            <button type="submit" className="w-full bg-[#C9A84C] text-[#0A0A0A] py-3 font-medium hover:bg-[#E5C870] uppercase tracking-wider text-sm" data-testid="create-blog-btn">Create Post</button>
          </form>
        </div>
      )}

      {activeTab === 'invoices' && (
        <div className="max-w-xl">
          <h2 className="text-xl font-serif text-[#F5F2EB] mb-4">Create Custom Invoice (Site Visit)</h2>
          <form onSubmit={handleCreateInvoice} className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-6 space-y-4">
            <div><label className="text-xs text-[#A3A3A3] uppercase">Enquiry ID</label>
              <select value={invoiceForm.enquiry_id} onChange={(e) => setInvoiceForm({ ...invoiceForm, enquiry_id: e.target.value })} className="w-full bg-[#1A1A1A] border-b border-[#C9A84C]/30 py-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" required>
                <option value="">Select Enquiry</option>
                {bookings.enquiries.filter(e => e.status === 'pending').map(e => (
                  <option key={e.enquiry_id} value={e.enquiry_id}>{e.enquiry_id} - {e.name} ({e.city})</option>
                ))}
              </select></div>
            <div><label className="text-xs text-[#A3A3A3] uppercase">Travel Cost (₹)</label>
              <input type="number" value={invoiceForm.travel_cost} onChange={(e) => setInvoiceForm({ ...invoiceForm, travel_cost: parseFloat(e.target.value) || 0 })} className="w-full bg-transparent border-b border-[#C9A84C]/30 py-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" /></div>
            <div><label className="text-xs text-[#A3A3A3] uppercase">Stay Cost (₹)</label>
              <input type="number" value={invoiceForm.stay_cost} onChange={(e) => setInvoiceForm({ ...invoiceForm, stay_cost: parseFloat(e.target.value) || 0 })} className="w-full bg-transparent border-b border-[#C9A84C]/30 py-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" /></div>
            <div><label className="text-xs text-[#A3A3A3] uppercase">Consultation Fee (₹)</label>
              <input type="number" value={invoiceForm.consultation_fee} onChange={(e) => setInvoiceForm({ ...invoiceForm, consultation_fee: parseFloat(e.target.value) || 0 })} className="w-full bg-transparent border-b border-[#C9A84C]/30 py-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" /></div>
            <div className="border-t border-[#C9A84C]/15 pt-3"><p className="text-[#F5F2EB]">Total: <span className="text-[#C9A84C] font-bold text-xl">₹{invoiceForm.travel_cost + invoiceForm.stay_cost + invoiceForm.consultation_fee}</span></p></div>
            <button type="submit" className="w-full bg-[#C9A84C] text-[#0A0A0A] py-3 font-medium hover:bg-[#E5C870] uppercase tracking-wider text-sm">Create Invoice & Payment Link</button>
          </form>
        </div>
      )}

      {activeTab === 'availability' && (
        <div className="max-w-xl">
          <h2 className="text-xl font-serif text-[#F5F2EB] mb-4">Manage Availability</h2>
          <form onSubmit={handleSetAvailability} className="bg-[#1A1A1A] border border-[#C9A84C]/10 p-6 space-y-4">
            <div><label className="text-xs text-[#A3A3A3] uppercase">Date</label>
              <input type="date" value={availForm.date} onChange={(e) => setAvailForm({ ...availForm, date: e.target.value })} className="w-full bg-transparent border-b border-[#C9A84C]/30 py-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" required /></div>
            <div className="flex items-center gap-3">
              <input type="checkbox" checked={availForm.available} onChange={(e) => setAvailForm({ ...availForm, available: e.target.checked })} className="accent-[#C9A84C]" />
              <label className="text-[#F5F2EB]">Available</label>
            </div>
            <div><label className="text-xs text-[#A3A3A3] uppercase">Custom Slots (comma-separated, e.g. "11:00 AM, 12:00 PM")</label>
              <input value={availForm.custom_slots} onChange={(e) => setAvailForm({ ...availForm, custom_slots: e.target.value })} className="w-full bg-transparent border-b border-[#C9A84C]/30 py-2 text-[#F5F2EB] focus:border-[#C9A84C] focus:outline-none" placeholder="Leave empty for default slots" /></div>
            <button type="submit" className="w-full bg-[#C9A84C] text-[#0A0A0A] py-3 font-medium hover:bg-[#E5C870] uppercase tracking-wider text-sm">Update Availability</button>
          </form>
        </div>
      )}

      {activeTab === 'users' && (
        <div>
          <h2 className="text-xl font-serif text-[#F5F2EB] mb-4">Registered Users ({users.length})</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#C9A84C]/20">
                <th className="text-left py-3 text-[#A3A3A3]">Name</th><th className="text-left py-3 text-[#A3A3A3]">Email</th><th className="text-left py-3 text-[#A3A3A3]">Phone</th><th className="text-left py-3 text-[#A3A3A3]">Wallet</th><th className="text-left py-3 text-[#A3A3A3]">Points</th><th className="text-left py-3 text-[#A3A3A3]">Joined</th>
              </tr></thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.user_id} className="border-b border-[#C9A84C]/10">
                    <td className="py-3 text-[#F5F2EB]">{u.name}</td>
                    <td className="py-3 text-[#A3A3A3]">{u.email}</td>
                    <td className="py-3 text-[#A3A3A3]">{u.phone || '-'}</td>
                    <td className="py-3 text-[#C9A84C]">₹{u.wallet_balance || 0}</td>
                    <td className="py-3 text-[#C9A84C]">{u.loyalty_points || 0}</td>
                    <td className="py-3 text-[#A3A3A3]">{u.created_at?.substring(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
